import { FC, ReactElement } from 'react';
import { upperFirst } from 'lodash';
import moment from 'moment';
import { FormApi } from 'final-form';

import { Box, HeaderTitle, Button, ActionConfirmation } from '@qwealth/qcore';
import { QTask, TASK_COMPLETED } from '@qwealth/qdata';

import { getDisplayTags, QTaskForm, toQTask } from 'data/models/taskForm';
import TruncateTextByTextHeight from 'components/common/TruncateTextByTextHeight';
import { ModalModeOptions, MODAL_MODE_EDIT, ACTION_CONFIRMATION_DELETE, TaskTabOptions } from 'data/models/Activity';
import CommentNoteAttachmentSection from './CommentNoteAttachmentSection';
import { useAppDispatch, useAppSelector } from 'data/store';
import { deleteHouseholdTask } from 'data/actions/householdActivities';
import { deleteActivityTask } from 'data/actions/activities';
import { LabelValueRow } from './AddTask.styled';
import RecurringTaskActionConfirmation from './RecurringTaskActionConfirmation';
import { TaskActions } from 'components/common/activityActions';
import TaskTagsList from 'components/common/TaskTagsList';
import { selectIsAssignedWorkflowInStore } from 'data/selectors/workflows';
import { fetchAndUpdateAssignedWorkflow } from 'data/helpers/workflows';

interface Props {
  task: Partial<QTask> | null;
  formValues: QTaskForm;
  isQFeed?: boolean;
  setModalMode: (state: ModalModeOptions) => void;
  taskContentHtml: string;
  close?: () => void;
  form: FormApi<QTaskForm, Partial<QTaskForm>>;
  setIsTaskModalHidden?: React.Dispatch<React.SetStateAction<boolean>>;
  qNoteModalMode: ModalModeOptions;
  setQNoteModalMode: React.Dispatch<React.SetStateAction<ModalModeOptions>>;
  selectedTab: TaskTabOptions;
  setSelectedTab: React.Dispatch<React.SetStateAction<TaskTabOptions>>;
  taskAttachmentsPath: string;
  taskActions?: TaskActions;
  isTaskModalFromWorkflow?: boolean;
}

const MainSectionDisplayMode: FC<Props> = ({
  task,
  formValues,
  isQFeed = false,
  setModalMode,
  taskContentHtml,
  close,
  form,
  setIsTaskModalHidden,
  qNoteModalMode,
  setQNoteModalMode,
  selectedTab,
  setSelectedTab,
  taskAttachmentsPath,
  taskActions,
  isTaskModalFromWorkflow,
}): ReactElement => {
  const dispatch = useAppDispatch();
  const isTaskWorkflowInStore = useAppSelector(selectIsAssignedWorkflowInStore(formValues.workflowId));
  const displayTags = getDisplayTags(formValues.tags, formValues.workflowTags);

  const handleDeleteTask = async (isDeleteFollowingTasks?: boolean) => {
    if (formValues?.isRecurring && isDeleteFollowingTasks !== undefined) {
      form.mutators.deleteFollowingTasks(isDeleteFollowingTasks);
    }
    const updatedValues: QTaskForm = form.getState().values;
    const payload: QTask = toQTask(updatedValues);
    const deletedTask: QTask | null = isQFeed ? await dispatch(deleteActivityTask(payload)) : await dispatch(deleteHouseholdTask(payload));
    if (deletedTask) {
      if (isTaskWorkflowInStore && deletedTask.workflowId && !isTaskModalFromWorkflow) {
        await fetchAndUpdateAssignedWorkflow(deletedTask.workflowId, dispatch);
      }
      taskActions?.deleteTask?.(deletedTask);
    }
    close?.();
    taskActions?.cancelTask?.();
  };

  return (
    <Box display="flex" flexDirection="column" gap="default" flexShrink={1} flexGrow={2} flexBasis="500px" minWidth="350px">
      <Box display="flex" flexDirection="row" gap="large" alignItems="center">
        <HeaderTitle color="dark" fontWeight="semiBold" flex={1}>
          {upperFirst(formValues.title)}
        </HeaderTitle>
        {formValues.status !== TASK_COMPLETED && (
          <Box display="flex" flexDirection="row" gap="large" flex={0}>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setModalMode(MODAL_MODE_EDIT);
                taskActions?.editTask?.();
              }}
            >
              Edit
            </Button>
            {formValues.isRecurring ? (
              <RecurringTaskActionConfirmation
                primaryButtonActionHandler={() => {
                  handleDeleteTask(false);
                }}
                secondaryButtonActionHandler={() => {
                  handleDeleteTask(true);
                }}
                taskTitle={formValues?.title}
                actionConfirmation={ACTION_CONFIRMATION_DELETE}
                ActionButtonRenderer={openHandler => (
                  <Button
                    variant="text"
                    size="small"
                    color="gray.2"
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      openHandler();
                    }}
                  >
                    Delete
                  </Button>
                )}
                setIsTaskModalHidden={setIsTaskModalHidden}
              />
            ) : (
              <ActionConfirmation
                actionHandler={() => {
                  handleDeleteTask();
                }}
                bodyText={
                  <Box>
                    Are you sure you want to delete <b>"{upperFirst(formValues?.title)}"</b>?
                  </Box>
                }
                ActionButtonRenderer={openHandler => (
                  <Button
                    variant="text"
                    size="small"
                    color="gray.2"
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                      openHandler();
                    }}
                  >
                    Delete
                  </Button>
                )}
                setPreviousModalHidden={setIsTaskModalHidden}
              />
            )}
          </Box>
        )}
      </Box>
      <Box display="flex" flexDirection="row" gap="large" flexWrap="wrap">
        <LabelValueRow>
          <Box as="span" className="info-label">
            Created By:
          </Box>
          <Box as="span" className="info-value">
            {formValues.createdBy} on <span className="nowrap-date">{moment(formValues.startTime).format('YYYY-MM-DD')}</span>
          </Box>
        </LabelValueRow>
        <LabelValueRow>
          <Box as="span" className="info-label">
            Updated By:
          </Box>
          <Box as="span" className="info-value">
            {formValues.lastUpdatedBy} on <span className="nowrap-date">{moment(formValues.lastUpdated).format('YYYY-MM-DD')}</span>
          </Box>
        </LabelValueRow>
      </Box>
      <Box display="flex" flexDirection="row" flexWrap="wrap" gap="default" alignItems="center">
        <Box fontSize="default" fontWeight="bold">
          Type:
        </Box>
        {/* @ts-ignore TODO remove when qdata is updated */}
        <Box>{formValues.taskType}</Box>
      </Box>
      <TaskTagsList tagsKey={displayTags} />

      {task && (
        <Box overflowY="auto" maxHeight="550px" paddingRight="large" width="100%">
          <Box display="flex" flexDirection="column" gap="small" marginBottom="large">
            <Box fontWeight="bold">Details:</Box>
            <TruncateTextByTextHeight text={taskContentHtml} textHeight="200px" />
          </Box>
          <CommentNoteAttachmentSection
            task={task}
            formValues={formValues}
            setIsTaskModalHidden={setIsTaskModalHidden}
            qNoteModalMode={qNoteModalMode}
            setQNoteModalMode={setQNoteModalMode}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            taskAttachmentsPath={taskAttachmentsPath}
          />
        </Box>
      )}
    </Box>
  );
};

export default MainSectionDisplayMode;
